import React, { useState } from 'react';
import { Mail, Send, Loader2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { commesseAPI } from '../../lib/api';
import { apiErrorMessage } from '../../lib/apiError';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const WeeklyEmailDialog = () => {
    const [open, setOpen] = useState(false);
    const [emails, setEmails] = useState([]);
    const [saved, setSaved] = useState([]);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState('');

    const load = async (o) => {
        setOpen(o);
        if (!o) return;
        try {
            const { data } = await commesseAPI.getWeeklyRecipients();
            setEmails(data.emails); setSaved(data.emails); setDraft('');
        } catch (e) { toast.error(apiErrorMessage(e, 'Errore caricamento destinatari')); }
    };

    const add = () => {
        const e = draft.trim().toLowerCase();
        if (!EMAIL_RE.test(e)) return toast.error('Email non valida');
        if (!emails.includes(e)) setEmails([...emails, e]);
        setDraft('');
    };

    const dirty = emails.join() !== saved.join();

    const save = async () => {
        setBusy('save');
        try {
            const { data } = await commesseAPI.setWeeklyRecipients(emails);
            setSaved(data.emails); setEmails(data.emails);
            toast.success('Destinatari salvati');
        } catch (e) { toast.error(apiErrorMessage(e, 'Errore salvataggio')); }
        setBusy('');
    };

    const send = async () => {
        setBusy('send');
        try {
            const { data } = await commesseAPI.sendWeekly();
            toast.success(`Excel inviato a ${data.recipients.join(', ')}`);
        } catch (e) { toast.error(apiErrorMessage(e, 'Invio non riuscito')); }
        setBusy('');
    };

    return (
        <Dialog open={open} onOpenChange={load}>
            <DialogTrigger asChild>
                <Button variant="outline" className="font-heading" data-testid="weekly-email-btn">
                    <Mail size={18} className="mr-2" /> INVIA EXCEL
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="weekly-email-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading uppercase">Excel settimanale commesse</DialogTitle>
                    <DialogDescription>Inviato in automatico ogni venerdì alle 17:00 a questi indirizzi.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Label className="text-xs uppercase tracking-wider">Destinatari</Label>
                    <div className="flex flex-wrap gap-2" data-testid="weekly-recipients-list">
                        {emails.length === 0 && <p className="text-sm text-muted-foreground">Nessun destinatario</p>}
                        {emails.map(e => (
                            <span key={e} className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded-full bg-muted" data-testid={`weekly-recipient-${e}`}>
                                {e}
                                <button type="button" onClick={() => setEmails(emails.filter(x => x !== e))} className="hover:text-destructive" data-testid={`weekly-recipient-remove-${e}`}>
                                    <X size={14} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Input
                            value={draft}
                            onChange={(ev) => setDraft(ev.target.value)}
                            onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); add(); } }}
                            placeholder="nome@pezzaioli.it"
                            data-testid="weekly-recipient-input"
                        />
                        <Button type="button" variant="outline" onClick={add} data-testid="weekly-recipient-add-btn"><Plus size={16} /></Button>
                    </div>
                </div>
                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={save} disabled={!dirty || busy !== ''} data-testid="weekly-recipients-save-btn">
                        {busy === 'save' && <Loader2 size={16} className="mr-2 animate-spin" />} Salva destinatari
                    </Button>
                    <Button className="btn-primary" onClick={send} disabled={dirty || saved.length === 0 || busy !== ''} title={dirty ? 'Salva prima i destinatari' : ''} data-testid="weekly-send-now-btn">
                        {busy === 'send' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />} Invia ora
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
